<template>
  <div
      style="margin-top: -20px; margin-bottom: -20px;"
      class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-slate-100 flex flex-col relative overflow-hidden"
      dir="rtl"
  >
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-40 -right-40 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl"></div>
      <div class="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-400/10 rounded-full blur-3xl"></div>
      <div
          class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-300/5 rounded-full blur-3xl"></div>
    </div>

    <PosSidebarNav
        :nav-open="navOpen"
        :active-tab="activeTab"
        @toggle-nav="navOpen = !navOpen"
        @set-tab="setTab"
        @logout="logout"
    />

    <PosDashboardHeader
        :current-user="user"
        :title="title"
        :img-source="img_source"
    />

    <div class="relative w-full flex-1 flex flex-col px-4 sm:px-6 lg:px-28 xl:px-32 2xl:px-40 pt-6 pb-8">
      <transition
          enter-active-class="transition-all duration-300 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-class="opacity-0 translate-y-3 scale-[0.98]"
          enter-to-class="opacity-100 translate-y-0 scale-100"
          leave-class="opacity-100 translate-y-0 scale-100"
          leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
      >
        <div
            v-if="notice"
            class="group relative mb-4 overflow-hidden rounded-3xl border border-emerald-300/30
           bg-gradient-to-l from-emerald-500/15 via-slate-900/90 to-slate-950/95
           px-4 py-4 text-emerald-50 shadow-2xl shadow-emerald-500/10 backdrop-blur-2xl"
            dir="rtl"
        >
          <!-- glow line -->
          <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/70 to-transparent"></div>

          <!-- soft background glow -->
          <div class="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-emerald-400/20 blur-3xl"></div>
          <div class="absolute -left-12 bottom-0 h-24 w-24 rounded-full bg-cyan-400/10 blur-3xl"></div>

          <div class="relative flex items-start gap-3">
            <!-- icon badge -->
            <div
                class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl
               border border-emerald-300/30 bg-emerald-400/15 shadow-lg shadow-emerald-500/20"
            >
              <svg class="h-5 w-5 text-emerald-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.3"
                    d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <!-- content -->
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="h-2 w-2 rounded-full bg-emerald-300 shadow-lg shadow-emerald-300/60 animate-pulse"></span>
                <span class="text-[11px] font-black tracking-wide text-emerald-200/80">
            عملیات موفق
          </span>
              </div>

              <p class="mt-1.5 text-sm font-bold leading-7 text-emerald-50">
                {{ notice }}
              </p>
            </div>

            <!-- close button / optional -->
            <button
                type="button"
                @click="notice = ''"
                class="shrink-0 rounded-xl border border-white/10 bg-white/5 p-2 text-emerald-100/70
               transition hover:bg-white/10 hover:text-white active:scale-95"
                title="بستن پیام"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </transition>
      <div class="flex-1 flex flex-col space-y-3 w-full max-w-7xl mx-auto">
        <div
            v-show="activeTab === 'stock-exit'"
            class="w-full rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-800/70 backdrop-blur-2xl shadow-2xl shadow-black/40 overflow-hidden my-2"
        >
          <PartnerStockExitCreate />
        </div>
        <div
            v-show="activeTab === 'stock-manage'"
            class="w-full rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-800/70 backdrop-blur-2xl shadow-2xl shadow-black/40 overflow-hidden my-2"
        >
          <PartnerStockExitManager />
        </div>

        <PosReturnsListTab
            ref="posReturnsListTab"
            v-show="activeTab === 'returns'"
            :api-get-pos="apiGetPos"
            :format-currency="formatCurrency"
            :toast="toast"
        />

        <PosOrderTab
            ref="posOrderTab"
            v-show="activeTab === 'pos'"
            :search-query.sync="searchQuery"
            :popup-name-search.sync="popupNameSearch"
            :search-loading="searchLoading"
            :search-results="searchResults"
            :filtered-search-results="filteredSearchResults"
            :selected-search-index="selectedSearchIndex"
            :customer="customer"
            :cart-items="cartItems"
            :grand-total="grandTotal"
            :subtotal-before-discounts="subtotalBeforeDiscounts"
            :subtotal-after-line-discounts="subtotalAfterLineDiscounts"
            :total-line-discount-amount="totalLineDiscountAmount"
            :order-discount-amount="orderDiscountAmount"
            :loading="loading"
            :img-source="img_source"
            :line-discount-percent="lineDiscountPercent"
            :item-wholesale-label="itemWholesaleLabel"
            :item-discount-label="itemDiscountLabel"
            @update:search-query="searchQuery = $event"
            @update:popup-name-search="popupNameSearch = $event"
            @update:selected-search-index="selectedSearchIndex = $event"
            @clear-search="clearSearch"
            @close-search-popup="closeSearchPopup"
            @select-search-product="selectSearchProduct"
            @submit-order="submitOrder"
            @clear-cart="clearCart"
            @open-discount-modal="openDiscountModal"
            @open-wholesale-modal="openWholesaleModal"
            @change-quantity="changeQuantity"
            @on-quantity-input="onQuantityInput"
            @remove-item="removeItem"
            @open-item-wholesale-modal="openItemWholesaleModal"
            @open-item-discount-modal="openItemDiscountModal"
        />
        <PosOrdersTab
            v-show="activeTab === 'orders'"
            :orders="orders"
            :orders-filters.sync="ordersFilters"
            :loading="loading"
            :status-badge-class="statusBadgeClass"
            :pos-status-label="posStatusLabel"
            @fetch-orders="fetchOrders"
            @apply-orders-filters="applyOrdersFilters"
            @clear-orders-filters="clearOrdersFilters"
            @go-orders-page="goOrdersPage"
            @open-order-details="openOrderDetails"
            @open-success-modal="openSuccessModal"
            @update-orders-page-size="orders.page_size = $event"
        />

        <div
            v-show="activeTab === 'overview'"
            class="w-full rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-800/70 backdrop-blur-2xl shadow-2xl shadow-black/40 overflow-hidden my-2"
        >
          <PosOverviewTab
              :overview="overview"
          />
        </div>

      </div>
    </div>
    <PosReturnCreateModal
        ref="posReturnCreateModal"
        :api-get-pos="apiGetPos"
        :api-post-pos="apiPostPos"
        class="max-w-7xl"
        :format-currency="formatCurrency"
        :toast="toast"
        @return-created="onPosReturnCreated"
    />
    <PosBulkDiscountModal
        :visible="showDiscountModal"
        :bulk-discount="bulkDiscount"
        @close="showDiscountModal = false"
        @apply="applyBulkDiscount"
    />

    <PosBulkWholesaleModal
        :visible="showWholesaleModal"
        :bulk-wholesale="bulkWholesale"
        @close="showWholesaleModal = false"
        @apply="applyBulkWholesale"
    />

    <PosOrderSuccessModal
        :order-success="orderSuccess"
        :success-header="successHeader"
        :success-order="successOrder"
        :success-status-loading="successStatusLoading"
        :success-status-error="successStatusError"
        :success-customer-name="successCustomerName"
        :success-customer-phone="successCustomerPhone"
        :success-print-label="successPrintLabel"
        :locked-status-message="lockedStatusMessage"
        :grand-total="grandTotal"
        :get-status-color="getStatusColor"
        :get-status-text="getStatusText"
        :success-keyboard-action="successKeyboardAction"
        @apply-success-status="onApplySuccessStatus"
        @open-order-details="openOrderDetails"
        @print-from-success="printFromSuccess"
        @copy-order-link="copyOrderLink"
        @open-return="openReturnFromSuccess"
        @close="closeOrderSuccessModal"
    />

    <PosConfirmStatusModal
        :confirm-modal="confirmModal"
        :confirm-theme="confirmTheme"
        :confirm-keyboard-action="confirmKeyboardAction"
        @close="closeConfirmModal"
        @confirm="confirmStatusChange"
    />

    <PosItemWholesaleModal
        :visible="showItemWholesaleModal"
        :item-wholesale-form="itemWholesaleForm"
        :item-modal-product-name="itemModalProductName"
        :item-modal-product-image="itemModalProductImage"
        :calc-wholesale-price="calcWholesalePrice"
        :img-source="img_source"
        @close="showItemWholesaleModal = false; itemModalTarget = null"
        @apply="applyItemWholesale"
    />

    <PosItemDiscountModal
        :visible="showItemDiscountModal"
        :item-discount-form="itemDiscountForm"
        :item-modal-product-name="itemModalProductName"
        :item-modal-product-image="itemModalProductImage"
        :img-source="img_source"
        @close="showItemDiscountModal = false"
        @apply="applyItemDiscount"
    />

    <PosOrderDetailsModal
        ref="orderDetailsModal"
        :api-get-pos="apiGetPos"
        :api-patch-pos="apiPatchPos"
        :api-post-pos="apiPostPos"
        :api-delete-pos="apiDeletePos"
        :pos-status-label="posStatusLabel"
        :format-currency="formatCurrency"
        :toast="toast"
        @print-from-success="printFromSuccess"
        @open-return="openReturnForOrder"
        :refresh-orders="fetchOrders"
    />
  </div>
</template>

<script>
import PosSidebarNav from '@/components/pos-dashboard/PosSidebarNav.vue'
import PosDashboardHeader from '@/components/pos-dashboard/PosDashboardHeader.vue'
import PosOverviewTab from '@/components/pos-dashboard/PosOverviewTab.vue'
import PosOrdersTab from '@/components/pos-dashboard/PosOrdersTab.vue'
import PosOrderTab from '@/components/pos-dashboard/PosOrderTab.vue'


import PosBulkDiscountModal from '@/components/pos-dashboard/modals/PosBulkDiscountModal.vue'
import PosBulkWholesaleModal from '@/components/pos-dashboard/modals/PosBulkWholesaleModal.vue'
import PosOrderSuccessModal from '@/components/pos-dashboard/modals/PosOrderSuccessModal.vue'
import PosConfirmStatusModal from '@/components/pos-dashboard/modals/PosConfirmStatusModal.vue'
import PosItemWholesaleModal from '@/components/pos-dashboard/modals/PosItemWholesaleModal.vue'
import PosItemDiscountModal from '@/components/pos-dashboard/modals/PosItemDiscountModal.vue'

import PosOrderDetailsModal from '@/modules/pos/PosOrderDetailsModal'
import PartnerStockExitCreate from "@/modules/partner/PartnerStockExitCreate";
import PartnerStockExitManager from "@/modules/partner/PartnerStockExitManager";
import PosReturnsListTab from '@/components/pos-dashboard/PosReturnsListTab.vue'
import PosReturnCreateModal from '@/components/pos-dashboard/modals/PosReturnCreateModal.vue'

export default {
  name: 'PosNewDashboard',
  components: {
    PartnerStockExitManager,
    PartnerStockExitCreate,
    PosSidebarNav,
    PosDashboardHeader,
    PosOverviewTab,
    PosOrdersTab,
    PosOrderTab,
    PosBulkDiscountModal,
    PosReturnsListTab,
    PosReturnCreateModal,
    PosBulkWholesaleModal,
    PosOrderSuccessModal,
    PosConfirmStatusModal,
    PosItemWholesaleModal,
    PosItemDiscountModal,
    PosOrderDetailsModal
  },
  data() {
    return {
      pendingPrintAfterConfirm: false,
      successKeyboardAction: 'primary',
      confirmKeyboardAction: 'confirm',
      popupNameSearch: '',
      selectedSearchIndex: -1,
      lastUserFocus: null,
      confirmModal: {
        visible: false,
        loading: false,
        fromStatus: '',
        toStatus: '',
        title: '',
        subtitle: '',
        bullets: [],
        confirmText: 'تأیید'
      },
      successStatusLoading: false,
      successStatusError: '',
      showItemDiscountModal: false,
      showItemWholesaleModal: false,
      itemModalTarget: null,

      itemDiscountForm: {
        line_discount_type: '',
        line_discount_value: null,
      },

      itemWholesaleForm: {
        is_wholesale: false,
        profit_margin: null,
        base: null,
      },

      navOpen: false,
      activeTab: 'pos',
      loading: {
        global: false,
        submit: false,
        orders: false
      },
      notice: '',
      searchQuery: '',
      searchLoading: false,
      searchResults: [],
      debouncedSearchFn: null,
      cartItems: [],
      orderDiscount: {
        type: 'none',
        value: null
      },
      wholesaleGlobal: {
        enabled: false,
        margin_percent: null
      },
      customer: {
        first_name: '',
        last_name: '',
        phone_number: '',
        user_id: null
      },
      lastOrder: null,
      orderQrUrl: '',
      lastOrderLink: '',
      orderSuccess: {
        visible: false,
        order: null
      },
      orders: {
        items: [],
        total: 0,
        page: 1,
        page_size: 10,
        total_pages: 1
      },
      ordersFilters: {
        search: '',
        date_from: '',
        date_to: '',
        status: ''
      },
      orderDetails: null,
      orderDetailsLoading: false,
      orderDetailsSaving: false,
      orderDetailsEdit: {
        first_name: '',
        last_name: '',
        phone_number: '',
        order_discount_type: '',
        order_discount_value: null,
        wholesale_margin_percent: null
      },
      showOrderDetailsModal: false,
      orderDetailsSearchQuery: '',
      orderDetailsSearchLoading: false,
      orderDetailsSearchResults: [],
      overview: {
        calendar: 'jalali',

        today_total: 0,
        today_gross_total: 0,
        today_returned_amount: 0,
        today_document_returned_amount: 0,
        today_direct_returned_amount: 0,
        today_orders: 0,
        today_orders_count: 0,
        today_items_count: 0,
        today_items_quantity: 0,
        today_return_docs_count: 0,
        today_return_items_count: 0,
        today_returned_quantity: 0,
        today_direct_returned_orders_count: 0,

        yesterday_total: 0,
        yesterday_gross_total: 0,
        yesterday_returned_amount: 0,
        yesterday_document_returned_amount: 0,
        yesterday_direct_returned_amount: 0,
        yesterday_orders: 0,
        yesterday_orders_count: 0,
        yesterday_items_count: 0,
        yesterday_items_quantity: 0,
        yesterday_return_docs_count: 0,
        yesterday_return_items_count: 0,
        yesterday_returned_quantity: 0,
        yesterday_direct_returned_orders_count: 0,

        month_total: 0,
        month_gross_total: 0,
        month_returned_amount: 0,
        month_document_returned_amount: 0,
        month_direct_returned_amount: 0,
        month_orders: 0,
        month_orders_count: 0,
        month_items_count: 0,
        month_items_quantity: 0,
        month_return_docs_count: 0,
        month_return_items_count: 0,
        month_returned_quantity: 0,
        month_direct_returned_orders_count: 0,

        avg_basket: 0,

        today_jalali: '',
        yesterday_jalali: '',
        today_range: null,
        yesterday_range: null,
        month_range: null
      },
      showDiscountModal: false,
      showWholesaleModal: false,
      bulkDiscount: {
        type: 'none',
        value: null
      },
      bulkWholesale: {
        enabled: false,
        margin_percent: null
      },
      successStatusForm: {
        status: '',
        loading: false
      }
    }
  },
  computed: {
    successOrder() {
      return (this.orderSuccess && this.orderSuccess.order) || {}
    },

    successHeader() {
      const s = (this.successOrder && this.successOrder.status) || 'dr'

      if (s === 'dr') {
        return {
          title: 'سفارش در حالت پیش‌نویس است',
          subtitle: 'می‌تونی نهایی یا لغو کنی.'
        }
      }

      if (s === 'cf') {
        return {
          title: 'سفارش نهایی شد',
          subtitle: 'فاکتور آماده چاپ است.'
        }
      }

      if (s === 'ca') {
        return {
          title: 'سفارش لغو شد',
          subtitle: 'این سفارش دیگر تغییرپذیر نیست.'
        }
      }

      if (s === 're') {
        return {
          title: 'برگشت از خرید ثبت شد',
          subtitle: 'وضعیت سفارش برگشت خورده است.'
        }
      }

      return {
        title: 'وضعیت سفارش',
        subtitle: ''
      }
    },

    successCustomerName() {
      const o = this.successOrder || {}
      return [o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ') || o.customer_name || ''
    },

    successCustomerPhone() {
      const o = this.successOrder || {}
      return o.customer_phone || o.customer_phone_number || ''
    },

    successPrintLabel() {
      const s = (this.successOrder && this.successOrder.status) || ''
      if (s === 'dr') return 'چاپ پیش‌فاکتور'
      if (s === 'cf') return 'چاپ فاکتور نهایی'
      if (s === 're') return 'چاپ برگشت از خرید'
      return 'چاپ فاکتور'
    },

    lockedStatusMessage() {
      const s = (this.successOrder && this.successOrder.status) || ''
      if (s === 'ca') return 'سفارش لغو شده و تغییرپذیر نیست. برای ادامه، سفارش جدید ثبت کنید.'
      if (s === 're') return 'سفارش برگشت از خرید شده و تغییرپذیر نیست. برای فروش مجدد، سفارش جدید ثبت کنید.'
      return 'این سفارش قابل تغییر وضعیت نیست.'
    },
    title() {
      return this.setDocumentTitle('فروش حضوری')
    },
    filteredSearchResults() {
      const q = (this.popupNameSearch || '').trim().toLowerCase()

      if (!q) return this.searchResults

      return this.searchResults.filter(p => {
        const name = `${p.variation_of_name || ''} ${p.name || ''}`.toLowerCase()
        return name.includes(q)
      })
    },
    todayVsYesterdayCircle() {
      const today = Number(this.overview?.today_total || 0)
      const yesterday = Number(this.overview?.yesterday_total || 0)

      const total = today + yesterday
      // اگر هر دو صفر بودن، نیم‌نیم نشون بده
      const todayShare = total > 0 ? today / total : 0.5
      const todayAngle = Math.round(todayShare * 360)

      const diff = today - yesterday
      const diffPercent = yesterday > 0 ? Math.round((diff / yesterday) * 100) : null

      let trend = 'flat'
      if (diff > 0) trend = 'up'
      else if (diff < 0) trend = 'down'

      return {
        today,
        yesterday,
        total,
        todayShare,
        todayAngle,
        diff,
        diffPercent,
        trend,
      }
    },
    confirmTheme() {
      const to = this.confirmModal.toStatus

      if (to === 'cf') {
        return {
          headerBg: 'bg-emerald-500/5',
          iconBox: 'bg-emerald-500/10 border-emerald-400/30',
          icon: 'text-emerald-300',
          subtitle: 'text-emerald-200/80',
          dot: 'bg-emerald-300',
          confirmBtn: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:shadow-lg hover:shadow-emerald-500/20'
        }
      }

      if (to === 'ca') {
        return {
          headerBg: 'bg-rose-500/5',
          iconBox: 'bg-rose-500/10 border-rose-400/30',
          icon: 'text-rose-300',
          subtitle: 'text-rose-200/80',
          dot: 'bg-rose-300',
          confirmBtn: 'bg-rose-400 hover:bg-rose-300'
        }
      }

      // re
      return {
        headerBg: 'bg-amber-500/5',
        iconBox: 'bg-amber-500/10 border-amber-400/30',
        icon: 'text-amber-300',
        subtitle: 'text-amber-200/80',
        dot: 'bg-amber-300',
        confirmBtn: 'bg-gradient-to-r from-amber-400 to-orange-500 hover:shadow-lg hover:shadow-amber-500/20'
      }
    },
    cartTotalQuantity() {
      return this.cartItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
    },
    subtotalBeforeDiscounts() {
      return this.cartItems.reduce((sum, item) => {
        const qty = Number(item.quantity) || 0
        const price = Number(item.unit_price) || 0
        return sum + qty * price
      }, 0)
    },
    totalLineDiscountAmount() {
      return this.cartItems.reduce((sum, item) => sum + (item.line_discount_amount || 0), 0)
    },
    subtotalAfterLineDiscounts() {
      return this.subtotalBeforeDiscounts - this.totalLineDiscountAmount
    },
    orderDiscountAmount() {
      if (!this.subtotalAfterLineDiscounts) return 0
      if (this.orderDiscount.type === 'percent' && this.orderDiscount.value) {
        return (this.subtotalAfterLineDiscounts * Number(this.orderDiscount.value || 0)) / 100
      }
      if (this.orderDiscount.type === 'amount' && this.orderDiscount.value) {
        return Math.min(Number(this.orderDiscount.value || 0), this.subtotalAfterLineDiscounts)
      }
      return 0
    },
    grandTotal() {
      const val = this.subtotalAfterLineDiscounts - this.orderDiscountAmount
      return val > 0 ? val : 0
    }
  },
  watch: {
    popupNameSearch() {
      const list = this.filteredSearchResults || []

      if (!list.length) {
        this.selectedSearchIndex = -1
        return
      }

      this.selectedSearchIndex = 0

      this.$nextTick(() => {
        this.scrollToSelectedItem()
      })
    },
    searchQuery(val) {
      const q = (val || '').trim()

      this.popupNameSearch = ''
      this.selectedSearchIndex = -1

      if (!q || q.length < 2) {
        this.searchResults = []
        return
      }

      if (this.debouncedSearchFn) {
        this.debouncedSearchFn(q)
      } else {
        this.searchProducts(q)
      }
    },
    'orderSuccess.visible': {
      immediate: true,
      handler(v) {
        if (v && this.orderSuccess && this.orderSuccess.order) {
          this.successStatusForm.status = this.orderSuccess.order.status || ''
        }
      }
    }
  },
  mounted() {
    document.title = 'اکسونی | فروشگاه حضوری'
    window.addEventListener('keydown', this.onGlobalKeydown, true)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onGlobalKeydown, true)
  },
  created() {
    this.debouncedSearchFn = this.debounce(this.searchProducts, 1000)
    this.fetchOverview()
    this.fetchOrders()
  },
  methods: {
    hasReturnDocument(order) {
      if (!order) return false

      return (
          Number(order.returns_count || 0) > 0 ||
          (Array.isArray(order.returns) && order.returns.length > 0)
      )
    },

    canDirectReturnOrder(order) {
      return Boolean(
          order &&
          order.status === 'cf' &&
          !this.hasReturnDocument(order)
      )
    },
    onApplySuccessStatus(targetStatus) {
      /**
       * اگر از success modal سفارش را نهایی کنیم، بعد از confirm موفق باید مستقیم چاپ شود.
       */
      this.pendingPrintAfterConfirm = targetStatus === 'cf'
      this.confirmKeyboardAction = 'confirm'
      this.applySuccessStatus(targetStatus)
    },

    getSuccessActions() {
      const status = this.successOrder && this.successOrder.status

      if (status === 'dr') {
        return ['primary', 'cancel', 'details', 'close']
      }

      if (status === 'cf') {
        return ['print', 'return', 'details', 'copy', 'close']
      }

      return ['details', 'print', 'copy', 'close']
    },

    moveSuccessKeyboardAction(direction) {
      const actions = this.getSuccessActions()

      if (!actions.length) {
        this.successKeyboardAction = 'primary'
        return
      }

      let idx = actions.indexOf(this.successKeyboardAction)

      if (idx < 0) idx = 0
      else idx += direction

      if (idx < 0) idx = actions.length - 1
      if (idx >= actions.length) idx = 0

      this.successKeyboardAction = actions[idx]
    },

    runSuccessKeyboardAction(action) {
      const status = this.successOrder && this.successOrder.status
      const selected = action || this.successKeyboardAction || 'primary'

      if (status === 'dr') {
        if (selected === 'cancel') {
          this.pendingPrintAfterConfirm = false
          this.applySuccessStatus('ca')
          return
        }

        if (selected === 'details') {
          this.openOrderDetails(this.successOrder)
          return
        }

        if (selected === 'close') {
          this.closeOrderSuccessModal()
          return
        }

        /**
         * پیش‌فرض Enter روی سفارش پیش‌نویس: نهایی‌سازی + بعدش چاپ
         */
        this.pendingPrintAfterConfirm = true
        this.confirmKeyboardAction = 'confirm'
        this.applySuccessStatus('cf')
        return
      }

      if (status === 'cf') {
        if (selected === 'return') {
          this.openReturnFromSuccess()
          return
        }

        if (selected === 'details') {
          this.openOrderDetails(this.successOrder)
          return
        }

        if (selected === 'copy') {
          this.copyOrderLink(this.successOrder)
          return
        }

        if (selected === 'close') {
          this.closeOrderSuccessModal()
          return
        }

        /**
         * پیش‌فرض Enter روی سفارش نهایی: چاپ
         */
        this.printFromSuccess()
        return
      }

      if (selected === 'details') {
        this.openOrderDetails(this.successOrder)
        return
      }

      if (selected === 'copy') {
        this.copyOrderLink(this.successOrder)
        return
      }

      if (selected === 'close') {
        this.closeOrderSuccessModal()
        return
      }

      this.printFromSuccess()
    },

    moveConfirmKeyboardAction() {
      this.confirmKeyboardAction = this.confirmKeyboardAction === 'confirm' ? 'cancel' : 'confirm'
    },

    runConfirmKeyboardAction() {
      if (this.confirmKeyboardAction === 'cancel') {
        this.closeConfirmModal()
        return
      }

      this.confirmStatusChange()
    },

    getPrintUrl(order) {
      const o = order || this.successOrder

      if (!o || !o.id) return ''

      return this.endpoint(`pos/v1/print/${o.id}/html/?token=${this.token}`)
    },
    openReturnForOrder(order) {
      if (!order || !order.id) {
        this.toast && this.toast('فاکتور برای مرجوعی پیدا نشد', 'error')
        return
      }

      if (order.status && order.status !== 'cf') {
        this.toast && this.toast('فقط فاکتور نهایی‌شده قابل مرجوعی است', 'error')
        return
      }

      if (this.orderSuccess) {
        this.orderSuccess.visible = false
      }

      if (this.$refs.orderDetailsModal && this.$refs.orderDetailsModal.close) {
        this.$refs.orderDetailsModal.close(true)
      }

      this.showDiscountModal = false
      this.showWholesaleModal = false
      this.showItemDiscountModal = false
      this.showItemWholesaleModal = false
      this.itemModalTarget = null

      this.$nextTick(() => {
        if (this.$refs.posReturnCreateModal && this.$refs.posReturnCreateModal.openForOrder) {
          this.$refs.posReturnCreateModal.openForOrder(order)
        }
      })
    },
    openReturnFromSuccess() {
      this.openReturnForOrder(this.successOrder)
    },

    async onPosReturnCreated(payload) {
      const order = payload && payload.order ? payload.order : null
      const orderId = order && order.id ? order.id : null

      if (this.fetchOrders) {
        this.fetchOrders()
      }

      if (this.fetchOverview) {
        this.fetchOverview()
      }

      if (this.$refs.posReturnsListTab && this.$refs.posReturnsListTab.fetchReturns) {
        this.$refs.posReturnsListTab.fetchReturns()
      }

      if (!orderId) {
        if (this.$refs.orderDetailsModal && this.$refs.orderDetailsModal.reloadCurrentOrder) {
          this.$refs.orderDetailsModal.reloadCurrentOrder()
        }
        return
      }

      let freshOrder = null

      try {
        freshOrder = await this.apiGetPos(`orders/${orderId}/`)
      } catch (e) {
        freshOrder = order
      }

      if (this.orderSuccess && this.orderSuccess.visible && freshOrder && freshOrder.id) {
        this.orderSuccess.order = freshOrder
      } else if (this.orderSuccess && this.orderSuccess.visible) {
        this.orderSuccess.visible = false
      }

      this.$nextTick(() => {
        if (this.$refs.orderDetailsModal && this.$refs.orderDetailsModal.openOrderDetails) {
          this.$refs.orderDetailsModal.openOrderDetails(orderId)
        }
      })
    },
    getSearchInputRef() {
      const tab = this.$refs.posOrderTab
      return tab && tab.$refs ? tab.$refs.searchInput : null
    },

    getPopupNameSearchInputRef() {
      const tab = this.$refs.posOrderTab
      return tab && tab.$refs ? tab.$refs.popupNameSearchInput : null
    },

    focusSearchInput() {
      const tab = this.$refs.posOrderTab

      if (tab && tab.focusSearchInput) {
        tab.focusSearchInput()
        return
      }

      const input = this.getSearchInputRef()
      if (input && input.focus) input.focus()
    },

    blurSearchInput() {
      const tab = this.$refs.posOrderTab

      if (tab && tab.blurSearchInput) {
        tab.blurSearchInput()
        return
      }

      const input = this.getSearchInputRef()
      if (input && input.blur) input.blur()
    },

    focusPopupNameSearchInput() {
      const tab = this.$refs.posOrderTab

      if (tab && tab.focusPopupNameSearchInput) {
        tab.focusPopupNameSearchInput()
        return
      }

      const input = this.getPopupNameSearchInputRef()
      if (input && input.focus) input.focus()
    },

    scrollToSelectedItem() {
      this.$nextTick(() => {
        const tab = this.$refs.posOrderTab

        if (tab && tab.scrollToSelectedItem) {
          tab.scrollToSelectedItem(this.selectedSearchIndex)
          return
        }

        const items = document.querySelectorAll('.search-result-item')

        if (items[this.selectedSearchIndex]) {
          items[this.selectedSearchIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          })
        }
      })
    },


    closeSearchPopup() {
      this.searchQuery = ''
      this.popupNameSearch = ''
      this.searchResults = []
      this.selectedSearchIndex = -1
      this.blurSearchInput()
    },

    clearSearch() {
      this.searchQuery = ''
      this.popupNameSearch = ''
      this.searchResults = []
      this.selectedSearchIndex = -1
      this.blurSearchInput()
    },
    selectSearchProduct(product) {
      if (!product || !product.inventory_count || Number(product.inventory_count) <= 0) return

      this.addProductToCart(product)

      this.searchQuery = ''
      this.popupNameSearch = ''
      this.searchResults = []
      this.selectedSearchIndex = -1

      this.blurSearchInput()
    },
    getStatusText(status) {
      const texts = {
        'dr': 'پیش‌نویس',
        'cf': 'نهایی',
        're': 'برگشت از فروش شده',
        'ca': 'لغو شده'
      };
      return texts[status] || 'نامشخص';
    },
    getStatusColor(status) {
      const colors = {
        'dr': 'text-amber-400',
        'cf': 'text-green-400',
        're': 'text-purple-400',
        'ca': 'text-rose-400'
      };
      return colors[status] || 'text-slate-400';
    },

    isEditableTarget(el) {
      if (!el) return false

      const tag = (el.tagName || '').toUpperCase()

      return (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          el.isContentEditable
      )
    },

    isSearchInputFocused() {
      const input = this.getSearchInputRef()
      return input && document.activeElement === input
    },

    isPopupNameSearchFocused() {
      const input = this.getPopupNameSearchInputRef()
      return input && document.activeElement === input
    },

    hasSearchPopupOpen() {
      return (
          this.activeTab === 'pos' &&
          (
              this.searchLoading ||
              (this.searchQuery && this.searchResults && this.searchResults.length)
          )
      )
    },

    hasAnyBlockingModalOpen() {
      return Boolean(
          (this.confirmModal && this.confirmModal.visible) ||
          (this.orderSuccess && this.orderSuccess.visible) ||
          this.showDiscountModal ||
          this.showWholesaleModal ||
          this.showItemDiscountModal ||
          this.showItemWholesaleModal
      )
    },

    normalizeSelectedSearchIndex() {
      const list = this.filteredSearchResults || []

      if (!list.length) {
        this.selectedSearchIndex = -1
        return
      }

      if (this.selectedSearchIndex < 0) {
        this.selectedSearchIndex = 0
        return
      }

      if (this.selectedSearchIndex >= list.length) {
        this.selectedSearchIndex = list.length - 1
      }
    },

    moveSearchSelection(direction) {
      const list = this.filteredSearchResults || []

      if (!list.length) {
        this.selectedSearchIndex = -1
        return
      }

      if (this.selectedSearchIndex < 0) {
        this.selectedSearchIndex = direction > 0 ? 0 : list.length - 1
      } else {
        this.selectedSearchIndex += direction
      }

      if (this.selectedSearchIndex < 0) {
        this.selectedSearchIndex = list.length - 1
      }

      if (this.selectedSearchIndex >= list.length) {
        this.selectedSearchIndex = 0
      }

      this.scrollToSelectedItem()
    },

    handleSearchPopupKeydown(e) {
      if (!this.hasSearchPopupOpen()) return false

      const list = this.filteredSearchResults || []
      const popupInput = this.getPopupNameSearchInputRef()
      const active = document.activeElement

      const isPrintableKey =
          e.key &&
          e.key.length === 1 &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.altKey

      /**
       * وقتی popup باز است و کاربر تایپ می‌کند،
       * تایپ باید برود داخل فیلتر popup، نه search اصلی و نه هیچ جای دیگر.
       */
      if (isPrintableKey) {
        if (popupInput && active !== popupInput) {
          e.preventDefault()
          e.stopPropagation()

          popupInput.focus()
          this.popupNameSearch += e.key

          return true
        }

        /**
         * اگر خود input فیلتر فوکوس است، اجازه بده v-model طبیعی تایپ کند.
         */
        return false
      }

      /**
       * Backspace هم اگر popup باز است و focus جای دیگری بود،
       * باید روی فیلتر popup اعمال شود.
       */
      if (e.key === 'Backspace') {
        if (popupInput && active !== popupInput) {
          e.preventDefault()
          e.stopPropagation()

          popupInput.focus()
          this.popupNameSearch = (this.popupNameSearch || '').slice(0, -1)

          return true
        }

        return false
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        this.moveSearchSelection(1)
        return true
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        this.moveSearchSelection(-1)
        return true
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()

        if (!list.length) return true

        this.normalizeSelectedSearchIndex()

        const product =
            list[this.selectedSearchIndex] ||
            (list.length === 1 ? list[0] : null)

        if (product) {
          this.selectSearchProduct(product)
        }

        return true
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        this.closeSearchPopup()
        return true
      }

      return false
    },
    handleOrderSubmitKeydown(e) {
      if (this.activeTab !== 'pos') return false
      if (this.hasAnyBlockingModalOpen()) return false
      if (this.hasSearchPopupOpen()) return false
      if (e.key !== 'Enter') return false

      const q = (this.searchQuery || '').trim()

      /**
       * اگر داخل سرچ اصلی چیزی نوشته شده، Enter باید اول سرچ/انتخاب کالا را انجام دهد.
       */
      if (q) {
        return false
      }

      /**
       * اگر سبد خالی است یا در حال ثبت هستیم، Enter کاری نکند.
       */
      if (!this.cartItems.length || this.loading.submit) {
        return false
      }

      e.preventDefault()
      e.stopPropagation()

      this.blurSearchInput()
      this.submitOrder()

      return true
    },
    handleConfirmModalKeydown(e) {
      if (!this.confirmModal || !this.confirmModal.visible) return false

      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()

        if (!this.confirmModal.loading) {
          this.closeConfirmModal()
        }

        return true
      }

      if (
          e.key === 'ArrowDown' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight'
      ) {
        e.preventDefault()
        e.stopPropagation()

        if (!this.confirmModal.loading) {
          this.moveConfirmKeyboardAction()
        }

        return true
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()

        if (!this.confirmModal.loading) {
          this.runConfirmKeyboardAction()
        }

        return true
      }

      return true
    },

    handleSuccessModalKeydown(e) {
      if (!this.orderSuccess || !this.orderSuccess.visible) return false

      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        this.closeOrderSuccessModal()
        return true
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
        e.preventDefault()
        e.stopPropagation()
        this.moveSuccessKeyboardAction(1)
        return true
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
        e.preventDefault()
        e.stopPropagation()
        this.moveSuccessKeyboardAction(-1)
        return true
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()

        if (this.successStatusLoading) return true

        this.runSuccessKeyboardAction(this.successKeyboardAction)
        return true
      }
      if (e.key === 'p' || e.key === 'P' || e.key === 'پ') {
        e.preventDefault()
        e.stopPropagation()

        if (!this.successStatusLoading) {
          this.printFromSuccess()
        }

        return true
      }

      return true
    },
    handleSimpleModalKeydown(e) {
      if (this.showItemDiscountModal) {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          this.showItemDiscountModal = false
          return true
        }

        if (e.key === 'Enter') {
          e.preventDefault()
          e.stopPropagation()
          this.applyItemDiscount()
          return true
        }

        return true
      }

      if (this.showItemWholesaleModal) {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          this.showItemWholesaleModal = false
          this.itemModalTarget = null
          return true
        }

        if (e.key === 'Enter') {
          e.preventDefault()
          e.stopPropagation()
          this.applyItemWholesale()
          return true
        }

        return true
      }

      if (this.showDiscountModal) {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          this.showDiscountModal = false
          return true
        }

        if (e.key === 'Enter') {
          e.preventDefault()
          e.stopPropagation()
          this.applyBulkDiscount()
          return true
        }

        return true
      }

      if (this.showWholesaleModal) {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          this.showWholesaleModal = false
          return true
        }

        if (e.key === 'Enter') {
          e.preventDefault()
          e.stopPropagation()
          this.applyBulkWholesale()
          return true
        }

        return true
      }

      return false
    },

    handleScannerKeydown(e) {
      if (this.$refs.orderDetailsModal && this.$refs.orderDetailsModal.visible) {
        return false
      }
      if (this.activeTab !== 'pos') return false
      if (this.hasAnyBlockingModalOpen()) return false

      const searchInput = this.getSearchInputRef()
      if (!searchInput) return false

      const active = document.activeElement
      const isEditable = this.isEditableTarget(active)

      /**
       * اگر کاربر داخل فیلدهای دیگر مثل نام مشتری، موبایل، تعداد، تخفیف و...
       * تایپ می‌کند، scanner نباید دخالت کند.
       * اما Enter را قبلاً handleOrderSubmitKeydown هندل کرده.
       */
      if (isEditable && active !== searchInput) {
        return false
      }

      /**
       * اگر popup باز است، Enter/Arrow/Escape/type را خود popup هندل می‌کند.
       */
      if (this.hasSearchPopupOpen()) {
        return false
      }

      const isPrintableKey =
          e.key &&
          e.key.length === 1 &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.altKey

      if (!isPrintableKey && e.key !== 'Enter') {
        return false
      }

      /**
       * Enter وقتی search خالی است نباید اینجا خورده شود.
       * چون باید برود submitOrder.
       */
      if (e.key === 'Enter') {
        const q = (this.searchQuery || '').trim()

        if (!q) {
          return false
        }

        e.preventDefault()
        e.stopPropagation()
        this.onSearchEnter()
        return true
      }

      /**
       * اگر کلید تایپی آمد و فوکوس روی search نبود،
       * یعنی احتمالاً scanner شروع کرده؛ فوکوس بده و همان کاراکتر اول را بنویس.
       */
      if (isPrintableKey && document.activeElement !== searchInput) {
        e.preventDefault()
        e.stopPropagation()
        searchInput.focus()
        this.searchQuery += e.key
        return true
      }

      return false
    },
    onGlobalKeydown(e) {
      /**
       * اولویت ۱: modal تأیید وضعیت
       */
      if (this.handleConfirmModalKeydown(e)) return

      /**
       * اولویت ۲: success modal
       */
      if (this.handleSuccessModalKeydown(e)) return

      /**
       * اولویت ۳: modalهای تخفیف/عمده
       */
      if (this.handleSimpleModalKeydown(e)) return

      /**
       * اولویت ۴: popup سرچ کالا
       */
      if (this.handleSearchPopupKeydown(e)) return

      /**
       * اولویت ۵: اگر popup باز نیست و search خالی است، Enter یعنی ثبت سفارش
       */
      if (this.handleOrderSubmitKeydown(e)) return

      /**
       * اولویت ۶: scanner و سرچ سریع SKU
       */
      this.handleScannerKeydown(e)
    },
    async onSearchEnter() {
      const q = (this.searchQuery || '').trim()
      if (!q) return

      await this.searchProducts(q)

      const list = this.filteredSearchResults || []


      if (list.length === 1) {
        const p = list[0]

        if (p && (p.inventory_count === undefined || Number(p.inventory_count) > 0)) {
          this.selectSearchProduct(p)
        }

        return
      }

      if (list.length > 1) {
        this.selectedSearchIndex = 0

        this.$nextTick(() => {
          this.focusPopupNameSearchInput()
          this.scrollToSelectedItem()
        })
      }
    },
    updateWholesaleMargin(val, item) {
      item.profit_margin = val
      item.unit_price = this.calcWholesalePrice(item)
    },
    calcWholesalePrice(item) {
      const base = Number(item.base || 0)
      const margin = Number(item.profit_margin || 0)
      return base + (base * margin / 100)
    },
    openSuccessModal(order) {
      this.orderSuccess = {
        visible: true,
        order: order
      }
      if (order.status)
        this.successStatusForm.status = order.status
    },
    statusBadgeClass(s) {
      if (s === 'cf')
        return 'bg-emerald-500/10 border-emerald-400/40 text-emerald-300'
      if (s === 'dr')
        return 'bg-amber-500/10 border-amber-400/40 text-amber-200'
      if (s === 'ca')
        return 'bg-rose-500/10 border-rose-400/40 text-rose-300'
      if (s === 're')
        return 'bg-indigo-500/10 border-indigo-400/40 text-indigo-300'

      return 'bg-slate-700/20 border-white/10 text-slate-300'
    },
    applySuccessStatus(targetStatus) {
      const o = this.successOrder
      if (!o || !o.id) return

      const s = o.status
      const allowed =
          (s === 'dr' && (targetStatus === 'cf' || targetStatus === 'ca')) ||
          (s === 'cf' && targetStatus === 're')

      if (!allowed) {
        this.successStatusError = 'این تغییر وضعیت مجاز نیست.'
        return
      }

      const cfg = this.buildConfirmConfig(s, targetStatus)

      this.confirmModal = {
        visible: true,
        loading: false,
        fromStatus: s,
        toStatus: targetStatus,
        title: cfg.title,
        subtitle: cfg.subtitle,
        bullets: cfg.bullets,
        confirmText: cfg.confirmText
      }
    },

    buildConfirmConfig(fromStatus, toStatus) {
      if (fromStatus === 'dr' && toStatus === 'cf') {
        return {
          title: 'نهایی کردن سفارش',
          subtitle: 'این عملیات برگشت‌پذیر نیست مگر با برگشت از خرید.',
          bullets: [
            'موجودی کالاها از انبار کسر می‌شود.',
            'سفارش دیگر قابل ویرایش نیست.',
            'فاکتور نهایی قابل چاپ می‌شود.'
          ],
          confirmText: 'نهایی کن'
        }
      }

      if (fromStatus === 'dr' && toStatus === 'ca') {
        return {
          title: 'لغو سفارش',
          subtitle: 'بعد از لغو، سفارش قابل تغییر وضعیت نخواهد بود.',
          bullets: [
            'سفارش بسته می‌شود و دیگر تغییرپذیر نیست.',
            'موجودی تغییر نمی‌کند (چون سفارش هنوز نهایی نشده).'
          ],
          confirmText: 'لغو کن'
        }
      }

      // cf -> re
      return {
        title: 'برگشت کامل مستقیم فاکتور',
        subtitle: 'این مسیر فقط وقتی مجاز است که هیچ سند مرجوعی برای فاکتور ثبت نشده باشد.',
        bullets: [
          'همه آیتم‌های فاکتور به موجودی برمی‌گردند.',
          'وضعیت سفارش به برگشت از خرید تغییر می‌کند.',
          'اگر قبلاً سند مرجوعی جزئی ثبت شده باشد، این عملیات مجاز نیست.'
        ],
        confirmText: 'برگشت از خرید ثبت کن'
      }
    },

    closeConfirmModal() {
      if (this.confirmModal.loading) return

      this.confirmModal.visible = false
      this.pendingPrintAfterConfirm = false
      this.confirmKeyboardAction = 'confirm'
    },

    async confirmStatusChange() {
      const o = this.successOrder
      if (!o || !o.id) return

      const targetStatus = this.confirmModal.toStatus
      if (!targetStatus) return
      if (targetStatus === 're' && !this.canDirectReturnOrder(o)) {
        this.successStatusError = 'برای این فاکتور سند مرجوعی ثبت شده است؛ برگشت کامل مستقیم مجاز نیست.'
        this.toast && this.toast(this.successStatusError, 'error')
        return
      }
      /**
       * برای جلوگیری از popup blocker:
       * پنجره چاپ را در همان لحظه Enter/click باز می‌کنیم،
       * بعد از موفقیت API فقط آدرسش را عوض می‌کنیم.
       */
      const shouldPrintAfterConfirm =
          targetStatus === 'cf' &&
          this.pendingPrintAfterConfirm

      let printWindow = null

      if (shouldPrintAfterConfirm) {
        printWindow = window.open('', '_blank')

        if (printWindow) {
          printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>در حال آماده‌سازی چاپ...</title>
          </head>
          <body style="font-family: sans-serif; background:#020617; color:white; display:flex; align-items:center; justify-content:center; height:100vh;">
            <div style="text-align:center">
              <h2>در حال نهایی‌سازی سفارش...</h2>
              <p>بعد از تأیید، فاکتور چاپ باز می‌شود.</p>
            </div>
          </body>
        </html>
      `)
          printWindow.document.close()
        }
      }

      this.confirmModal.loading = true
      this.successStatusError = ''

      try {
        const updated = await this.apiPostPos(`orders/${o.id}/set-status/`, {
          status: targetStatus
        })

        this.orderSuccess.order = updated
        this.toast('وضعیت سفارش اعمال شد')
        this.confirmModal.visible = false

        if (this.refreshOrders) {
          await this.refreshOrders()
        }

        await this.fetchOrders()
        await this.fetchOverview()

        if (
            shouldPrintAfterConfirm &&
            updated &&
            updated.status === 'cf'
        ) {
          const url = this.getPrintUrl(updated)

          this.pendingPrintAfterConfirm = false

          this.$nextTick(() => {
            if (printWindow && !printWindow.closed) {
              printWindow.location.href = url
            } else {
              window.open(url, '_blank')
            }
          })
        } else {
          this.pendingPrintAfterConfirm = false

          if (printWindow && !printWindow.closed) {
            printWindow.close()
          }
        }
      } catch (e) {
        console.error('confirmStatusChange error:', e)

        this.successStatusError = 'خطا در تغییر وضعیت سفارش'
        this.toast('خطا در تغییر وضعیت سفارش')

        this.pendingPrintAfterConfirm = false

        if (printWindow && !printWindow.closed) {
          printWindow.close()
        }
      } finally {
        this.confirmModal.loading = false
      }
    },
    statusConfirmMessage(fromStatus, toStatus) {
      // از dr
      if (fromStatus === 'dr' && toStatus === 'cf') {
        return 'با نهایی کردن سفارش:\n• برگشت از خرید کالاها کسر می‌شود\n• سفارش دیگر قابل ویرایش نیست\n\nادامه می‌دهید؟'
      }
      if (fromStatus === 'dr' && toStatus === 'ca') {
        return 'با لغو سفارش:\n• سفارش بسته می‌شود\n• دیگر قابل تغییر وضعیت نیست\n\nلغو شود؟'
      }
      // از cf
      if (fromStatus === 'cf' && toStatus === 're') {
        return 'با برگشت از خرید سفارش:\n• موجودی کالاها به انبار برمی‌گردد\n• سفارش دیگر قابل تغییر وضعیت نیست\n\nبرگشت از خرید ثبت شود؟'
      }
      return 'آیا مطمئن هستید؟'
    },
    printFromSuccess(order = null) {
      const targetOrder = order && order.id ? order : this.successOrder
      const url = this.getPrintUrl(targetOrder)

      if (!url) {
        this.toast && this.toast('سفارشی برای چاپ پیدا نشد')
        return
      }

      window.open(url, '_blank')
    },
    itemWholesalePreviewPrice() {
      const item = this.itemModalTarget
      if (!item) return 0

      const base = Number(item.base || 0)
      const margin = Number(item.profit_margin || 0)

      if (!base || !margin) return 0

      return base * (1 + margin / 100)
    },

    openItemDiscountModal(item) {
      this.itemModalTarget = item
      this.itemDiscountForm = {
        line_discount_type: item.line_discount_type || '',
        line_discount_value: item.line_discount_value ?? null,
      }
      this.showItemDiscountModal = true
    },

    applyItemDiscount() {
      const item = this.itemModalTarget
      if (!item) return

      const t = this.itemDiscountForm.line_discount_type || ''
      const v = this.itemDiscountForm.line_discount_value

      if (!t) {
        item.line_discount_type = ''
        item.line_discount_value = null
        item.line_discount_amount = 0
      } else {
        item.line_discount_type = t
        item.line_discount_value =
            (v === '' || v === null || v === undefined) ? null : Number(v)
      }

      this.recalculateLine(item)

      if (this.orderDiscount.type !== 'none' || this.orderDiscount.value) {
        this.orderDiscount.type = 'none'
        this.orderDiscount.value = null
      }

      this.showItemDiscountModal = false
      this.itemModalTarget = null
    },
    openItemWholesaleModal(item) {
      this.itemModalTarget = item
      this.itemWholesaleForm = {
        is_wholesale: !!item.is_wholesale,
        profit_margin: item.profit_margin ?? null,
        base: item.base ?? null,
      }
      this.showItemWholesaleModal = true
    },

    applyItemWholesale() {
      const item = this.itemModalTarget
      if (!item) return

      item.is_wholesale = !!this.itemWholesaleForm.is_wholesale

      if (item.is_wholesale) {
        const pm = this.itemWholesaleForm.profit_margin
        item.profit_margin =
            (pm === '' || pm === null || pm === undefined) ? null : Number(pm)

        this.applyWholesalePricing(item)   // همون متد خودت
      } else {
        item.unit_price = Number(item.product.pos_price || item.product.price || item.unit_price)
        item.profit_margin = null
      }

      this.recalculateLine(item)

      // ✅ هر تغییری روی عمده‌ی آیتم → عمده‌ی کلی خاموش بشه
      this.wholesaleGlobal.enabled = false
      this.wholesaleGlobal.margin_percent = null

      this.showItemWholesaleModal = false
      this.itemModalTarget = null
    },

    itemModalProductName() {
      const it = this.itemModalTarget
      return it && it.product ? (it.product.name || 'محصول') : 'محصول'
    },

    itemModalProductImage() {
      const it = this.itemModalTarget
      return it && it.product ? (it.product.image || null) : null
    },


    openItemPopup(item, tab) {
      this.itemPopupTarget = item
      this.itemPopupTab = tab || 'discount'

      this.itemPopupForm = {
        line_discount_type: item.line_discount_type || '',
        line_discount_value: item.line_discount_value ?? null,

        is_wholesale: !!item.is_wholesale,
        profit_margin: item.profit_margin ?? null,
      }

      this.itemPopupOpen = true
    },

    closeItemPopup() {
      this.itemPopupOpen = false
      this.itemPopupTarget = null
    },

    // فقط UI رو آپدیت می‌کنه و همون recalculateLine خودت رو صدا می‌زنه
    applyItemPopup() {
      const item = this.itemPopupTarget
      if (!item) return

      // discount
      const t = this.itemPopupForm.line_discount_type || ''
      const v = this.itemPopupForm.line_discount_value

      if (!t) {
        item.line_discount_type = ''
        item.line_discount_value = null
      } else {
        item.line_discount_type = t
        item.line_discount_value =
            (v === '' || v === null || v === undefined) ? null : Number(v)
      }

      // wholesale
      item.is_wholesale = !!this.itemPopupForm.is_wholesale
      if (item.is_wholesale) {
        const pm = this.itemPopupForm.profit_margin
        item.profit_margin =
            (pm === '' || pm === null || pm === undefined) ? null : Number(pm)
      } else {
        item.profit_margin = null
      }

      // این همون محاسبه‌ایه که خودت داری
      // (فرض: recalculateLine(item) تو کدت هست و line_discount_amount/line_total رو آپدیت می‌کنه)
      this.recalculateLine(item)

      this.closeItemPopup()
    },

    // فقط برای نمایش روی ردیف
    itemDiscountLabel(item) {
      if (!item.line_discount_type || item.line_discount_value === null || item.line_discount_value === undefined || item.line_discount_value === '') {
        return 'بدون تخفیف'
      }
      if (item.line_discount_type === 'percent') return `٪${Number(item.line_discount_value)}`
      return 'مبلغی'
    },

    itemWholesaleLabel(item) {
      if (!item.is_wholesale) return 'خرده'
      if (item.profit_margin !== null && item.profit_margin !== undefined && item.profit_margin !== '') {
        return `عمده ٪${Number(item.profit_margin)}`
      }
      return 'عمده'
    },
    calcLineDiscountAmount(item) {
      const qty = Number(item.quantity || 0)
      const unitPrice = Number(item.unit_price || 0)  // ✔ قیمت فعلی (خرده یا عمده)

      const subtotal = unitPrice * qty

      const t = item.line_discount_type
      const v = item.line_discount_value

      if (!t || v === null || v === undefined || v === '') return 0

      let discount = 0
      const value = Number(v || 0)

      if (t === 'percent') {
        discount = (subtotal * value) / 100
      } else {
        discount = value
      }

      if (discount < 0) discount = 0
      if (discount > subtotal) discount = subtotal
      return discount
    },

    calcLineSubtotal(item) {
      const qty = Number(item.quantity || 0)
      const unitPrice = Number(item.unit_price || 0)  // ✔
      return unitPrice * qty
    },

    isWholesaleItem(item) {
      // هر چی تو سبد داری: یا is_wholesale داری یا profit_margin/wholesale_margin_percent
      return !!item.is_wholesale || item.wholesale_margin_percent != null || (item.profit_margin != null && Number(item.profit_margin) > 0)
    },
    toggleLineDiscount(item) {
      if (item.line_discount_type) {
        item.line_discount_type = '';
        item.line_discount_value = 0;
        item.line_discount_amount = 0;
      } else {
        item.line_discount_type = 'percent';
        item.line_discount_value = 10;
      }
      this.onLineDiscountChange(item);
    },

    posStatusLabel(status) {
      const map = {
        dr: 'پیش‌نویس',
        cf: 'نهایی شده',
        ca: 'لغو شده',
        re: 'برگشت از خرید'
      }
      return map[status] || status
    },

    openOrderDetails(order) {
      this.closeOrderSuccessModal()
      const id = typeof order === 'object' ? order.id : order
      if (!id) return

      if (this.$refs.orderDetailsModal && this.$refs.orderDetailsModal.openOrderDetails) {
        this.$refs.orderDetailsModal.openOrderDetails(id)
      }
    },
    async fetchOrderDetails(id) {
      const data = await this.apiGetPos(`orders/${id}/`, {})
      this.orderDetails = data

      this.orderDetailsEdit = {
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        phone_number: data.phone_number || '',
        order_discount_type: data.order_discount_type || '',
        order_discount_value: data.order_discount_value,
        wholesale_margin_percent: data.wholesale_margin_percent
      }

      const map = {}
      ;(data.items || []).forEach(it => {
        map[it.id] = {
          quantity: it.quantity,
          line_discount_type: it.line_discount_type || '',
          line_discount_value: it.line_discount_value,
          wholesale_margin_percent: it.wholesale_margin_percent
        }
      })
      this.orderDetailsItemsEdit = map
    },

    clearOrderDetails() {
      this.orderDetails = null
      this.orderDetailsItemsEdit = {}
      this.orderDetailsSearchQuery = ''
      this.orderDetailsSearchResults = []
    },
    closeOrderDetailsModal() {
      this.showOrderDetailsModal = false
      this.clearOrderDetails()
    },
    async saveOrderDetails() {
      if (!this.orderDetails) return

      this.orderDetailsSaving = true
      try {
        const id = this.orderDetails.id
        await this.apiPatchPos(`orders/${id}/`, {
          first_name: this.orderDetailsEdit.first_name || null,
          last_name: this.orderDetailsEdit.last_name || null,
          phone_number: this.orderDetailsEdit.phone_number || null,
          order_discount_type: this.orderDetailsEdit.order_discount_type || null,
          order_discount_value: this.orderDetailsEdit.order_discount_value,
          wholesale_margin_percent: this.orderDetailsEdit.wholesale_margin_percent
        })

        this.toast('سفارش بروزرسانی شد')
        await this.fetchOrderDetails(id)
        await this.fetchOrders()
      } catch (e) {
        console.error('Error saving order details:', e)
        this.toast('خطا در بروزرسانی سفارش')
      } finally {
        this.orderDetailsSaving = false
      }
    },

    async saveOrderItem(itemId) {
      if (!this.orderDetails || !this.orderDetailsItemsEdit[itemId]) return

      const payload = this.orderDetailsItemsEdit[itemId]

      try {
        await this.apiPatchPos(`order-items/${itemId}/`, {
          quantity: payload.quantity,
          line_discount_type: payload.line_discount_type || null,
          line_discount_value: payload.line_discount_value,
          wholesale_margin_percent: payload.wholesale_margin_percent
        })

        this.toast('ردیف بروزرسانی شد')
        await this.fetchOrderDetails(this.orderDetails.id)
        await this.fetchOrders()
      } catch (e) {
        console.error('Error updating order item:', e)
        this.toast('خطا در بروزرسانی ردیف')
      }
    },

    async removeOrderItem(itemId) {
      if (!this.orderDetails) return

      try {
        await this.apiDeletePos(`order-items/${itemId}/`)
        this.toast('ردیف حذف شد')
        await this.fetchOrderDetails(this.orderDetails.id)
        await this.fetchOrders()
      } catch (e) {
        console.error('Error deleting order item:', e)
        this.toast('خطا در حذف ردیف')
      }
    },

    async searchProductsForOrderDetails() {
      const q = (this.orderDetailsSearchQuery || '').trim()
      if (q.length < 2) {
        this.orderDetailsSearchResults = []
        return
      }

      this.orderDetailsSearchLoading = true
      try {
        const data = await this.apiGetPos('products/search/', {suffix: q})
        this.orderDetailsSearchResults = data.results || data || []
      } catch (e) {
        console.error('Error searching products for order details:', e)
        this.toast('خطا در جستجوی کالا')
      } finally {
        this.orderDetailsSearchLoading = false
      }
    },

    async addProductToOrderFromDetails(product) {
      if (!this.orderDetails || !product) return

      try {
        await this.apiPostPos('order-items/', {
          order: this.orderDetails.id,
          product: product.id,
          quantity: 1
        })

        this.toast('کالا به سفارش اضافه شد')
        this.orderDetailsSearchQuery = ''
        this.orderDetailsSearchResults = []

        await this.fetchOrderDetails(this.orderDetails.id)
        await this.fetchOrders()
      } catch (e) {
        console.error('Error adding product to order from details:', e)
        this.toast('خطا در افزودن کالا به سفارش')
      }
    },
    calculateLineTotal(item) {
      const baseAmount = item.quantity * item.unit_price;
      item.line_total = baseAmount - (item.line_discount_amount || 0);
    },
    logout() {
      this.setToken("");
      this.$store.commit("setUser", null);
      if (this.$route.name != "Login") {
        let url = this.$router.resolve({name: "Login"});
        window.location = url;
      }
      this.tabEmit("logout");
    },
    closeAllPosModals() {
      // Main order/detail/return modals
      if (this.$refs.orderDetailsModal && this.$refs.orderDetailsModal.close) {
        this.$refs.orderDetailsModal.close()
      }

      if (this.$refs.posReturnCreateModal && this.$refs.posReturnCreateModal.close) {
        this.$refs.posReturnCreateModal.close()
      }

      // Success / status modals
      if (this.orderSuccess) {
        this.orderSuccess.visible = false
      }

      if (this.confirmModal) {
        this.confirmModal.visible = false
        this.confirmModal.loading = false
      }

      // POS discount / wholesale modals
      this.showDiscountModal = false
      this.showWholesaleModal = false
      this.showItemDiscountModal = false
      this.showItemWholesaleModal = false
      this.itemModalTarget = null

      // Old detail modal state, if it still exists in the file
      if (this.showOrderDetailsModal !== undefined) {
        this.showOrderDetailsModal = false
      }

      if (this.clearOrderDetails) {
        this.clearOrderDetails()
      }

      // Search popups
      this.searchQuery = ''
      this.popupNameSearch = ''
      this.searchResults = []
      this.selectedSearchIndex = -1
    },

    setTab(key) {
      this.closeAllPosModals()
      this.activeTab = key

      if (key === 'orders' && this.fetchOrders) {
        this.fetchOrders()
      }

      if (key === 'returns' && this.$refs.posReturnsListTab && this.$refs.posReturnsListTab.fetchReturns) {
        this.$refs.posReturnsListTab.fetchReturns()
      }
    },
    debounce(fn, delay) {
      let timeout
      return (...args) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => fn.apply(this, args), delay)
      }
    },
    toast(msg) {
      this.notice = msg
      setTimeout(() => {
        this.notice = ''
      }, 3000)
    },
    formatCurrency(amount) {
      if (!amount) return '۰'
      return new Intl.NumberFormat('fa-IR').format(Number(amount)) + ' تومان'
    },
    cleanParams(obj) {
      const copy = {...(obj || {})}
      Object.keys(copy).forEach(k => {
        if (copy[k] === '' || copy[k] === null || copy[k] === undefined) delete copy[k]
      })
      return copy
    },
    apiGetPos(path, params) {
      return new Promise((resolve, reject) => {
        this.request({
          url: this.endpoint(`pos/v1/${path}`),
          method: 'get',
          params: this.cleanParams(params || {}),
          success: data => resolve(data),
          error: err => reject(err)
        })
      })
    },
    apiPostPos(path, data) {
      const payload = this.cleanParams(data || {})
      return new Promise((resolve, reject) => {
        this.request({
          url: this.endpoint(`pos/v1/${path}`),
          method: 'post',
          data: payload,
          success: data => resolve(data),
          error: err => reject(err)
        })
      })
    },
    lineDiscountPercent(item) {
      const reg = Number(item.regular_price || 0)
      const price = Number(item.unit_price || 0)
      if (!reg || !price || price >= reg) return 0
      const diff = reg - price
      return Math.round((diff * 100) / reg)
    },
    async searchProducts(query) {
      const q = (query || '').trim()

      if (!q) {
        this.searchResults = []
        this.selectedSearchIndex = -1
        return
      }

      this.searchLoading = true

      try {
        const params = {
          suffix: q
        }

        const data = await this.apiGetPos('products/search/', params)
        this.searchResults = data.results || data || []

        this.$nextTick(() => {
          this.selectedSearchIndex = this.filteredSearchResults.length ? 0 : -1
          this.scrollToSelectedItem()
        })
      } catch (e) {
        console.error('Error searching products:', e)
        this.searchResults = []
        this.selectedSearchIndex = -1
        this.toast('خطا در جستجوی کالا')
      } finally {
        this.searchLoading = false
      }
    },
    addProductToCart(product) {
      const stock = (product && product.inventory_count != null)
          ? Number(product.inventory_count)
          : null

      // اگه موجودی صفر یا منفی بود اصلاً اضافه نکن
      if (stock !== null && stock <= 0) {
        this.toast('این کالا در حال حاضر ناموجود است')
        return
      }

      const existing = this.cartItems.find(
          item => item.product && item.product.id === product.id && !item.is_wholesale
      )

      if (existing) {
        // اگر موجودی تعریف شده، بیشتر از اون نرو
        if (stock && existing.quantity >= stock) {
          this.toast('بیشتر از موجودی انبار نمی‌توان اضافه کرد')
        } else {
          const nextQty = stock ? Math.min(existing.quantity + 1, stock) : (existing.quantity + 1)
          existing.quantity = nextQty
          this.recalculateLine(existing)
        }
      } else {
        const base = Number(product.base || product.price || 0)
        const regularPrice = Number(product.regular_price || product.price || 0)
        const price = Number(product.pos_price || product.price || 0)
        const currencyRate = Number(product.current_currency_rate || 1)
        const profitType = product.profit_type || 'percent'
        const profitMargin = Number(product.profit_margin || 0)

        const item = {
          _key: `${product.id}-${Date.now()}-${Math.random()}`,
          product,
          quantity: 1,
          base: base,
          regular_price: regularPrice,
          unit_price: price,
          current_currency_rate: currencyRate,
          profit_type: profitType,
          profit_margin: profitMargin,
          is_wholesale: false,
          line_discount_type: '',
          line_discount_value: null,
          line_discount_amount: 0,
          line_total: price
        }
        this.recalculateLine(item)
        this.cartItems.unshift(item)
      }

      this.clearSearch()
    },
    clearCart() {
      this.cartItems = []
    },
    removeItem(idx) {
      this.cartItems.splice(idx, 1)
    },
    changeQuantity(idx, newQty) {
      const item = this.cartItems[idx]
      if (!item) return

      const stock = (item.product && item.product.inventory_count != null)
          ? Number(item.product.inventory_count)
          : null

      // 👇 اگر صفر یا خالی شد، ردیف رو حذف کن
      if (!newQty || newQty <= 0) {
        this.removeItem(idx)
        return
      }

      // اگر موجودی تعریف شده، سقف بذار
      if (stock && newQty > stock) newQty = stock

      item.quantity = newQty
      this.recalculateLine(item)
    },

    onQuantityInput(idx) {
      const item = this.cartItems[idx]
      if (!item) return

      let q = Number(item.quantity) || 0
      const stock = (item.product && item.product.inventory_count != null)
          ? Number(item.product.inventory_count)
          : null

      if (q <= 0) {
        this.removeItem(idx)
        return
      }

      if (stock && q > stock) q = stock

      item.quantity = q
      this.recalculateLine(item)
    },
    toggleWholesale(idx) {
      const item = this.cartItems[idx]
      item.is_wholesale = !item.is_wholesale
      if (item.is_wholesale) {
        if (this.wholesaleGlobal.enabled && this.wholesaleGlobal.margin_percent) {
          item.profit_margin = this.wholesaleGlobal.margin_percent
        }
        this.applyWholesalePricing(item)
      } else {
        item.unit_price = Number(item.product.pos_price || item.product.price || item.unit_price)
      }
      this.recalculateLine(item)
    },
    onWholesaleMarginChange(item) {
      this.applyWholesalePricing(item)
      this.recalculateLine(item)
    },
    recalculateLine(item) {
      const qty = Number(item.quantity) || 0
      const unit = Number(item.unit_price) || 0
      const beforeDiscount = qty * unit

      let lineDiscount = 0
      if (item.line_discount_type === 'percent' && item.line_discount_value) {
        lineDiscount = (beforeDiscount * Number(item.line_discount_value || 0)) / 100
      } else if (item.line_discount_type === 'amount' && item.line_discount_value) {
        lineDiscount = Math.min(Number(item.line_discount_value || 0), beforeDiscount)
      }

      item.line_discount_amount = lineDiscount
      item.line_total = beforeDiscount - lineDiscount
    },
    onLineDiscountChange(item) {
      this.recalculateLine(item)
    },
    applyWholesalePricing(item) {
      const base = Number(item.base || 0)
      const margin = Number(item.profit_margin || 0)
      if (!base || !margin) return
      const price = base * (1 + margin / 100)
      item.unit_price = price
    },
    applyWholesaleToAll() {
      if (!this.wholesaleGlobal.margin_percent) return
      this.cartItems.forEach(item => {
        item.is_wholesale = true
        item.profit_margin = this.wholesaleGlobal.margin_percent
        this.applyWholesalePricing(item)
        this.recalculateLine(item)
      })
      this.toast('سود عمده روی همه ردیف‌ها اعمال شد')
    },
    openDiscountModal() {
      this.bulkDiscount.type = this.orderDiscount.type || 'none'
      this.bulkDiscount.value =
          this.orderDiscount.type && this.orderDiscount.type !== 'none'
              ? this.orderDiscount.value
              : null

      this.showDiscountModal = true
    },
    applyBulkDiscount() {
      const type = this.bulkDiscount.type || 'none'

      if (type === 'none') {
        this.orderDiscount.type = 'none'
        this.orderDiscount.value = null

        this.bulkDiscount.type = 'none'
        this.bulkDiscount.value = null

        this.cartItems.forEach(item => {
          item.line_discount_type = ''
          item.line_discount_value = null
          item.line_discount_amount = 0
          this.recalculateLine(item)
        })

        this.showDiscountModal = false
        this.toast && this.toast('تخفیف کلی حذف شد')
        return
      }

      const value = Number(this.bulkDiscount.value)

      if (Number.isNaN(value) || value < 0) {
        this.toast && this.toast('مقدار تخفیف معتبر نیست')
        return
      }

      if (type === 'percent' && value > 100) {
        this.toast && this.toast('درصد تخفیف نمی‌تواند بیشتر از ۱۰۰ باشد')
        return
      }

      this.orderDiscount.type = type
      this.orderDiscount.value = value

      this.bulkDiscount.type = type
      this.bulkDiscount.value = value

      this.cartItems.forEach(item => {
        item.line_discount_type = type
        item.line_discount_value = value
        this.recalculateLine(item)
      })

      this.showDiscountModal = false
      this.toast && this.toast('تخفیف کلی اعمال شد')
    },
    openWholesaleModal() {
      this.bulkWholesale.enabled = !!this.wholesaleGlobal.enabled
      this.bulkWholesale.margin_percent = this.wholesaleGlobal.enabled
          ? this.wholesaleGlobal.margin_percent
          : null

      this.showWholesaleModal = true
    },
    applyBulkWholesale() {
      const enabled = !!this.bulkWholesale.enabled

      if (!enabled) {
        this.wholesaleGlobal.enabled = false
        this.wholesaleGlobal.margin_percent = null

        this.bulkWholesale.enabled = false
        this.bulkWholesale.margin_percent = null

        this.cartItems.forEach(item => {
          item.is_wholesale = false
          item.profit_margin = null

          item.unit_price = Number(
              item.product?.pos_price ||
              item.product?.price ||
              item.product?.unit_price ||
              item.product?.base_price ||
              item.unit_price ||
              0
          )

          this.recalculateLine(item)
        })

        this.showWholesaleModal = false
        this.toast && this.toast('حالت عمده حذف شد')
        return
      }

      const margin = Number(this.bulkWholesale.margin_percent)

      if (Number.isNaN(margin) || margin < 0) {
        this.toast && this.toast('درصد سود عمده معتبر نیست')
        return
      }

      this.wholesaleGlobal.enabled = true
      this.wholesaleGlobal.margin_percent = margin

      this.bulkWholesale.enabled = true
      this.bulkWholesale.margin_percent = margin

      this.cartItems.forEach(item => {
        item.is_wholesale = true
        item.profit_margin = margin

        this.applyWholesalePricing(item)
        this.recalculateLine(item)
      })

      this.showWholesaleModal = false
      this.toast && this.toast('سود عمده اعمال شد')
    },
    buildOrderPayload() {
      const items = this.cartItems.map(item => ({
        product: item.product.id,
        quantity: item.quantity,
        base: item.base,
        current_currency_rate: item.current_currency_rate,
        profit_type: item.profit_type,
        profit_margin: item.profit_margin,
        price: item.unit_price,
        regular_price: item.regular_price,
        line_discount_type: item.line_discount_type || null,
        line_discount_value: item.line_discount_value || null,
        is_wholesale: item.is_wholesale
      }))

      return {
        customer_first_name: this.customer.first_name || null,
        customer_last_name: this.customer.last_name || null,
        customer_phone_number: this.customer.phone_number || null,
        customer_user_id: this.customer.user_id || null,

        discount_type: this.orderDiscount.type,
        discount_value: this.orderDiscount.value || null,

        items,
        client_subtotal: this.subtotalBeforeDiscounts,
        client_line_discount_total: this.totalLineDiscountAmount,
        client_order_discount_total: this.orderDiscountAmount,
        client_final_amount: this.grandTotal
      }
    },
    async submitOrder() {
      if (!this.cartItems.length) {
        this.toast('سبد خالی است')
        return
      }
      this.loading.submit = true
      try {
        const payload = this.buildOrderPayload()
        const order = await this.apiPostPos('orders/', payload)
        this.lastOrder = order
        this.generateOrderQr(order)
        this.toast('سفارش با موفقیت ثبت شد')
        this.customer = {
          first_name: '',
          last_name: '',
          phone_number: '',
          user_id: null
        }


        this.orderSuccess.order = order
        this.orderSuccess.visible = true

        this.fetchOverview()
        this.fetchOrders()
        this.clearCart()
      } catch (e) {
        console.error('Error creating POS order:', e)
        this.toast('خطا در ثبت سفارش')
      } finally {
        this.loading.submit = false
      }
    },
    generateOrderQr(order) {
      const id = order.id
      const url = this.endpoint(`pos/v1/print/${id}/html/?token=${this.token}`)
      this.lastOrderLink = url
      this.orderQrUrl = ''
    },
    copyOrderLink(o) {
      const url = this.lastOrderLink || this.endpoint(`pos/v1/print/${o.id}/html/?token=${this.token}`)
      navigator.clipboard.writeText(url).then(() => {
        this.toast('لینک فاکتور کپی شد')
      }).catch(() => {
        this.toast('خطا در کپی لینک')
      })
    },
    closeOrderSuccessModal() {
      this.orderSuccess.visible = false
      this.orderSuccess.order = null
      this.successStatusError = ''
      this.successStatusLoading = false
      this.pendingPrintAfterConfirm = false
      this.successKeyboardAction = 'primary'
      this.confirmKeyboardAction = 'confirm'

      this.$nextTick(() => {
        this.focusSearchInput()
      })
    },
    goToOrdersFromModal() {
      this.activeTab = 'orders'
      this.orderSuccess.visible = false
    },
    apiPatchPos(path, data) {
      const payload = this.cleanParams(data || {})
      return new Promise((resolve, reject) => {
        this.request({
          url: this.endpoint(`pos/v1/${path}`),
          method: 'patch',
          data: payload,
          success: data => resolve(data),
          error: err => reject(err)
        })
      })
    },
    apiDeletePos(path) {
      return new Promise((resolve, reject) => {
        this.request({
          url: this.endpoint(`pos/v1/${path}`),
          method: 'delete',
          success: data => resolve(data),
          error: err => reject(err)
        })
      })
    },

    async fetchOrders() {
      this.loading.orders = true
      try {
        const params = {
          page: this.orders.page,
          page_size: this.orders.page_size,
          search: this.ordersFilters.search || undefined,
          date_from: this.ordersFilters.date_from || undefined,
          date_to: this.ordersFilters.date_to || undefined,
          status: this.ordersFilters.status || undefined,
          // اگر خواستی مرتب‌سازی هم داشته باشی:
          // ordering: '-created_at',
        }
        const data = await this.apiGetPos('orders/', params)
        const results = data.results || data || []
        this.orders.items = results
        this.orders.total = data.count || results.length
        this.orders.total_pages = Math.max(
            1,
            Math.ceil(this.orders.total / this.orders.page_size)
        )
      } catch (e) {
        console.error('Error fetching POS orders:', e)
      } finally {
        this.loading.orders = false
      }
    },
    applyOrdersFilters() {
      this.orders.page = 1
      this.fetchOrders()
    },
    clearOrdersFilters() {
      this.ordersFilters = {search: '', date_from: '', date_to: '', status: ''}
      this.applyOrdersFilters()
    },
    goOrdersPage(page) {
      this.orders.page = Math.max(1, Math.min(this.orders.total_pages, page))
      this.fetchOrders()
    },
    viewOrder(order) {
      this.lastOrder = order
      this.generateOrderQr(order)
      this.activeTab = 'pos'
      this.toast('سفارش انتخاب شد؛ لینک و QR در صفحه صدور نمایش داده شد.')
    },
    async fetchOverview() {
      try {
        const data = await this.apiGetPos('orders/summary/', {})
        this.overview = Object.assign({
          calendar: 'jalali',

          today_total: 0,
          today_gross_total: 0,
          today_returned_amount: 0,
          today_document_returned_amount: 0,
          today_direct_returned_amount: 0,
          today_orders: 0,
          today_orders_count: 0,
          today_items_count: 0,
          today_items_quantity: 0,
          today_return_docs_count: 0,
          today_return_items_count: 0,
          today_returned_quantity: 0,
          today_direct_returned_orders_count: 0,

          yesterday_total: 0,
          yesterday_gross_total: 0,
          yesterday_returned_amount: 0,
          yesterday_document_returned_amount: 0,
          yesterday_direct_returned_amount: 0,
          yesterday_orders: 0,
          yesterday_orders_count: 0,
          yesterday_items_count: 0,
          yesterday_items_quantity: 0,
          yesterday_return_docs_count: 0,
          yesterday_return_items_count: 0,
          yesterday_returned_quantity: 0,
          yesterday_direct_returned_orders_count: 0,

          month_total: 0,
          month_gross_total: 0,
          month_returned_amount: 0,
          month_document_returned_amount: 0,
          month_direct_returned_amount: 0,
          month_orders: 0,
          month_orders_count: 0,
          month_items_count: 0,
          month_items_quantity: 0,
          month_return_docs_count: 0,
          month_return_items_count: 0,
          month_returned_quantity: 0,
          month_direct_returned_orders_count: 0,

          avg_basket: 0,

          today_jalali: '',
          yesterday_jalali: '',
          today_range: null,
          yesterday_range: null,
          month_range: null
        }, data || {})
      } catch (e) {
        console.error('Error fetching POS overview:', e)
      }
    },
    refreshAll() {
      this.loading.global = true
      Promise.all([
        this.fetchOverview(),
        this.fetchOrders()
      ]).finally(() => {
        this.loading.global = false
      })
    }
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>